import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalService } from 'ui-components/services/modal-service';

import { GenericErrorRetryContainer } from '../../../containers/generic-error-retry-container';
import { editTagSuccess } from '../../../store/tags/tags.actions';
import { updateTag } from '../../../store/tags/tags.api';
import { messages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';

export const useEditTag = ({ initialTag, onClose }) => {
  const dispatch = useDispatch();
  const [editTag, setEditTag] = useAsyncSafeState(initialTag);
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const trackTagManagementWorkflow = useTrackWorkflow(Workflows.TAG_MANAGEMENT_FLOW);

  const onTagChanged = useCallback(
    (tag) => {
      setEditTag(tag);
    },
    [setEditTag],
  );

  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await updateTag(editTag.tagId, editTag);
      dispatch(editTagSuccess({ tag: editTag }));
      trackTagManagementWorkflow(WorkflowSteps.COMPLETE, { tag: editTag });
    } catch (e) {
      setIsSaving(false);
      trackTagManagementWorkflow(WorkflowSteps.ERROR, { error: e });
      ModalService.launchModal({
        component: GenericErrorRetryContainer,
        props: {
          onClick: () => onSave(editTag),
          title: <FormattedMessage {...messages.editTagErrorTitle} />,
          message: <FormattedMessage {...messages.editTagErrorMessage} />,
        },
      });
    } finally {
      onClose();
    }
  }, [setIsSaving, editTag, dispatch, trackTagManagementWorkflow, onClose]);

  const memoizedAddTagsObj = useMemo(() => {
    return {
      editTag,
      isSaving,
      onTagChanged,
      onSave,
    };
  }, [editTag, isSaving, onTagChanged, onSave]);

  return memoizedAddTagsObj;
};
